import { pauseThisDevice } from "@/lib/push";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const OPERATOR_ROLES = ["staff", "admin", "super_admin"];

// A sign-in tablet that loses the internet and is reloaded must stay a tablet (not fall back to the unlock screen, which needs the server), so the last good answer is remembered here.
const KIOSK_ID_KEY = "oh_kiosk_identity_v1";
function rememberKiosk(u) { try { if (u?.role === "kiosk") localStorage.setItem(KIOSK_ID_KEY, JSON.stringify(u)); } catch { /* storage blocked */ } }
export function forgetKiosk() { try { localStorage.removeItem(KIOSK_ID_KEY); } catch { /* storage blocked */ } }
function offlineKioskUser() {
  try {
    const m = window.location.pathname.match(/^\/kiosk\/([^/]+)/);
    const u = m && JSON.parse(localStorage.getItem(KIOSK_ID_KEY) || "null");
    return u && u.role === "kiosk" && (!u.tenant_slug || u.tenant_slug === m[1]) ? { ...u, offline: true } : null;
  } catch { return null; }
}

function authenticatedUser(data) {
  // A proxy's SPA fallback can return index.html with HTTP 200 for an API request.
  // Only accept the backend's user payload as an authenticated session.
  if (!data || typeof data !== "object" || Array.isArray(data)
      || typeof data.user_id !== "string" || !data.user_id
      || ![...OPERATOR_ROLES, "parent", "kiosk"].includes(data.role)) {
    throw new Error("The sign-in API returned an invalid response. Please check that /api is routed to the backend server.");
  }
  return data;
}

// Site-scope switcher: a module-level var so the axios interceptor (outside React) can read the
// current choice, kept in sync with the AuthProvider's own state (which is what re-renders the
// switcher UI and drives the app-wide remount-on-change in App.js).
let currentSiteScope = null;
export const getSiteScope = () => currentSiteScope;
const siteScopeKey = (tenantId) => `hp-site-scope:${tenantId}`;

// Super admin only: the provider currently opened from the Platform console. The super admin belongs to
// the platform ("Out of School Hub"), not to a provider, so provider-level calls must say which provider
// they are for; the interceptor below does that with X-Tenant-Id. Kept for this browser tab only.
let currentViewing = null;
export const getViewingProvider = () => currentViewing;
const VIEWING_KEY = "hp-open-provider";
const LAST_OPENED_KEY = "hp-last-provider";
const readJson = (store, key) => { try { return JSON.parse(store.getItem(key) || "null"); } catch { return null; } };
export const lastOpenedProvider = () => readJson(localStorage, LAST_OPENED_KEY);

axios.interceptors.request.use((config) => {
  if (currentViewing && !(config.headers && (config.headers["X-Tenant-Id"] || config.headers.get?.("X-Tenant-Id")))) {
    config.headers = config.headers || {};
    const mode = currentViewing.mode === "edit" ? null : "view";   // a read-only view tells the API so, and writes are refused
    if (typeof config.headers.set === "function") { config.headers.set("X-Tenant-Id", currentViewing.id); if (mode) config.headers.set("X-Platform-Mode", mode); }
    else { config.headers["X-Tenant-Id"] = currentViewing.id; if (mode) config.headers["X-Platform-Mode"] = mode; }
  }
  if (currentSiteScope && !(config.params && "site_id" in config.params)) {
    config.params = { ...(config.params || {}), site_id: currentSiteScope };
  }
  return config;
});

const PIN_CHECK = /\/kiosk\/(lookup|checkin|checkout|verify-staff|staff\/clock|staff\/shift|incidents\/[^/]+\/sign)(\?|$)/;
let refreshing = null;
let lastLoadToast = 0;
axios.interceptors.response.use((r) => r, async (error) => {
  const { config, response } = error;
  // Many screens load their data with a bare `.catch(() => {})`, so a failed read used to leave an empty
  // page that looked like "no data". Say so once (not per request) when the server errors or is unreachable.
  if (config && (config.method || "get").toLowerCase() === "get" && (!response || response.status >= 500)
      && config.responseType !== "blob" && !config.silent && !/\/auth\/|unread-count|gmail\/status/.test(config.url || "")) {
    const now = Date.now();
    if (now - lastLoadToast > 20000) {
      lastLoadToast = now;
      // No answer at all: say which address wasn't reached, so a school or office IT person knows what to allow
      let host = "";
      try { host = new URL(config.url, config.baseURL || window.location.href).host; } catch { /* leave it out */ }
      toast.error(response ? "Part of this page couldn't load (server error) — try refreshing."
        : `Can't reach the server${host ? ` (${host})` : ""} — check your connection. On a school or work network, its filter may be blocking ${host || "this site"}.`, response ? undefined : { duration: 12000 });
    }
  }
  // The provider switched support access off (or it ran out) while the platform was inside it.
  if (currentViewing && response?.status === 403 && /hasn't given the platform support access/.test(String(response.data?.detail || ""))) {
    window.dispatchEvent(new Event("hp-access-ended"));
  }
  // A wrong PIN is also a 401, but it is no sign the session has run out: replaying it would send every wrong PIN twice (and count it twice toward locking the tablet).
  if (response?.status === 401 && config && !config._retried && !config.url.includes("/auth/") && !PIN_CHECK.test(config.url)) {
    config._retried = true;
    try {
      refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
      await refreshing;
      return axios(config);
    } catch (e) {
      if (e.response) window.dispatchEvent(new Event("hp-logout"));     // the server said no; a dropped connection is not a sign-out
    }
  }
  return Promise.reject(error);
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [siteScope, setSiteScopeState] = useState(null); // null = "all sites"
  const [viewing, setViewingState] = useState(() => { currentViewing = readJson(sessionStorage, VIEWING_KEY); return currentViewing; }); // super admin: provider opened from the console

  const checkAuth = useCallback(async () => {
    try { const r = await axios.get(`${API}/auth/me`); const u = authenticatedUser(r.data); rememberKiosk(u); setUser(u); return; }
    catch (e) {
      if (!e.response || [502, 503, 504].includes(e.response.status)) { setUser(offlineKioskUser() || false); return; }          // no answer at all (offline): a tablet carries on from what it last knew
      if (e.response.status !== 401) { forgetKiosk(); setUser(false); return; }
    }
    // The 15-minute sign-in ran out while the tab was closed or asleep: renew it from the longer-lived one rather than showing the sign-in page.
    try { await axios.post(`${API}/auth/refresh`); const r = await axios.get(`${API}/auth/me`); const u = authenticatedUser(r.data); rememberKiosk(u); setUser(u); }
    catch (e) { if (e.response) forgetKiosk(); setUser(e.response ? false : (offlineKioskUser() || false)); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Keep a signed-in person signed in: renew quietly every 10 minutes while the page is open, and as soon as it is looked at again after
  // being in the background (phones and tablets pause timers). Only a failed renewal -- an expired or revoked sign-in -- signs them out.
  const signedIn = !!user;
  useEffect(() => {
    if (!signedIn) return undefined;
    let last = Date.now();
    const renew = () => { last = Date.now(); axios.post(`${API}/auth/refresh`).catch((e) => { if (e.response?.status === 401) window.dispatchEvent(new Event("hp-logout")); }); };
    const timer = setInterval(renew, 10 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible" && Date.now() - last > 5 * 60 * 1000) renew(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [signedIn]);

  // Restore this LOGIN's remembered site scope once we know who's signed in, and reset it if the
  // signed-in tenant or user changes (e.g. a super admin switching between tenants, or a different
  // staff member signing into a shared device). Keyed by user as well as tenant -- keying by tenant
  // alone would leak one admin's site choice to the next person who logs into that same tenant on
  // the same device (another staff member whose own sites don't include it, or a kiosk session).
  const scopeOwner = user ? `${user.tenant_id || viewing?.id || user.tenant_slug || "notenant"}:${user.user_id || user.email}` : null;
  useEffect(() => {
    if (!scopeOwner) { currentSiteScope = null; setSiteScopeState(null); return; }
    const saved = localStorage.getItem(siteScopeKey(scopeOwner)) || null;
    currentSiteScope = saved;
    setSiteScopeState(saved);
  }, [scopeOwner]);

  const setSiteScope = useCallback((siteId) => {
    currentSiteScope = siteId || null;
    setSiteScopeState(currentSiteScope);
    if (scopeOwner) {
      if (currentSiteScope) localStorage.setItem(siteScopeKey(scopeOwner), currentSiteScope);
      else localStorage.removeItem(siteScopeKey(scopeOwner));
    }
  }, [scopeOwner]);
  useEffect(() => {
    const h = () => { forgetKiosk(); setUser(false); };
    window.addEventListener("hp-logout", h);
    return () => window.removeEventListener("hp-logout", h);
  }, []);

  useEffect(() => {
    const h = () => { if (!currentViewing) return; const name = currentViewing.name; currentViewing = null; setViewingState(null); try { sessionStorage.removeItem(VIEWING_KEY); } catch { /* storage blocked */ } toast.error(`${name}'s support access has ended. Ask a provider admin to turn it on again.`); };
    window.addEventListener("hp-access-ended", h);
    return () => window.removeEventListener("hp-access-ended", h);
  }, []);
  // Only a super admin can have a provider open; anyone else (or signed out) drops it.
  useEffect(() => {
    if (user === null) return;
    if (!user || user.role !== "super_admin") {
      currentViewing = null; setViewingState(null);
      try { sessionStorage.removeItem(VIEWING_KEY); } catch { /* storage blocked */ }
    }
  }, [user]);
  // mode "view" (the default) is read-only; "edit" can change things. Both are recorded in the platform audit log.
  const openProvider = useCallback(async (t, mode = "view") => {
    const r = await axios.post(`${API}/auth/platform/open/${t.id}`, null, { params: { mode } });
    const v = { id: t.id, name: t.name, slug: t.slug, branding: t.branding || null, mode, access: r.data?.supportAccess || null };
    currentViewing = v; setViewingState(v);
    try { sessionStorage.setItem(VIEWING_KEY, JSON.stringify(v)); localStorage.setItem(LAST_OPENED_KEY, JSON.stringify({ id: v.id, name: v.name })); } catch { /* storage blocked */ }
  }, []);
  const setViewMode = useCallback(async (mode) => {
    if (!currentViewing) return;
    const r = await axios.post(`${API}/auth/platform/open/${currentViewing.id}`, null, { params: { mode } });
    const v = { ...currentViewing, mode, access: r.data?.supportAccess || currentViewing.access };
    currentViewing = v; setViewingState(v);
    try { sessionStorage.setItem(VIEWING_KEY, JSON.stringify(v)); } catch { /* storage blocked */ }
  }, []);
  const closeProvider = useCallback(() => {
    currentViewing = null; setViewingState(null);
    try { sessionStorage.removeItem(VIEWING_KEY); } catch { /* storage blocked */ }
  }, []);

  const login = async (email, password) => { const r = await axios.post(`${API}/auth/login`, { email, password }); const u = authenticatedUser(r.data); setUser(u); return u; };
  const register = async (data) => { const r = await axios.post(`${API}/auth/register`, data); const u = authenticatedUser(r.data); setUser(u); return u; };
  const logout = async () => { forgetKiosk(); await pauseThisDevice(API); try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ } setUser(false); };
  const updateProfile = async (data) => { const r = await axios.put(`${API}/auth/me`, data); const u = authenticatedUser(r.data); setUser(u); return u; };
  // `provider` is the provider portal the family arrived through (its slug); the server keeps it across the
  // Google round-trip so a brand-new family lands under that provider.
  const googleLogin = (provider) => {
    const redirectPath = window.location.pathname + window.location.search;
    window.location.href = `${API}/auth/google/login?redirect=${encodeURIComponent(redirectPath)}${provider ? `&provider=${encodeURIComponent(provider)}` : ""}`;
  };

  return <AuthContext.Provider value={{ user, viewing, openProvider, setViewMode, closeProvider, api: API, login, register, logout, updateProfile, googleLogin, refresh: checkAuth, isOperator: user && OPERATOR_ROLES.includes(user.role), siteScope, setSiteScope }}>{children}</AuthContext.Provider>;
}
